import { useEffect, useState } from 'react';
import { getGraphqlPathname } from 'src/common/utils';
import { getPricesQuery } from '../queries/getPricesQuery.graphql';
import type { CoinInfoType, CurrencyInfoType } from '../types';

interface GetPricesQueryResponse {
  data: {
    prices: {
      edges: CoinInfoType[];
    };
    currencies: {
      fiats: CurrencyInfoType[];
    };
  };
}

const COIN_RESPONSE_LIMIT = 15 as const;

const getPriceData = async () => {
  try {
    const response = await fetch(getGraphqlPathname(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: getPricesQuery,
        variables: { limit: COIN_RESPONSE_LIMIT, offset: 0 },
      }),
    });

    const json: GetPricesQueryResponse = await response.json();
    const currencyInfo = (json.data.currencies?.fiats.map((currency) => ({
      ...currency,
      type: 'FIAT',
    })) as CurrencyInfoType[]) ?? [
      {
        icon: 'https://cdn.cointracker.io/static/images/fiats/usd.png',
        id: '1',
        name: 'US Dollar',
        priceUsd: 1,
        symbol: 'USD',
        type: 'FIAT',
      },
    ];
    const coinInfo = json.data.prices.edges
      .filter((coin) =>
        [
          'BTC',
          'ETH',
          'SOL',
          'ADA',
          'DOGE',
          'TRON',
          'BNB',
          'AVAX',
          'XRP',
          'LINK',
        ].includes(coin.symbol),
      )
      .sort((a, b) => b.marketCap - a.marketCap);

    return { coinInfo, currencyInfo };
  } catch (e) {
    console.error(e);
    return { coinInfo: [], currencyInfo: [] };
  }
};

export function useGetPrices() {
  const [coins, setCoins] = useState<CoinInfoType[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyInfoType[]>([]);
  const [hasMounted, setHasMounted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasMounted) return;
    setLoading(true);
    getPriceData().then(({ coinInfo, currencyInfo }) => {
      setLoading(false);
      setCoins(coinInfo);
      setCurrencies(currencyInfo);
    });
  }, [hasMounted]);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return {
    coins,
    currencies,
    hasMounted,
    loading,
  };
}
